import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Divider, Grid, IconButton, Input, InputAdornment, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { BlockOutlined, CheckCircleOutline, HistoryOutlined, PersonAddAltOutlined, RefreshOutlined } from "@mui/icons-material";
import Loader from "../../../UiComponents/Loader/Loader";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import ConfirmBox from "../../../UiComponents/ConfirmBox/ConfirmBox";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import { listRoadzenUsers, setRoadzenUserStatus } from "../../../Service/RoadzenService";
import { CreateUserDialog, HistoryDialog } from "./RoadzenUserDialogs";
import { activeWarning, confirmFor, dateText, walletText } from "./roadzenUsersUtils";

// the same small icon buttons as the Brisk Certificate List rows
const ROW_ACTION_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  color: "#1E3A5F",
  backgroundColor: "#EEF3F9",
  border: "1px solid #D6E1EE",
  "&:hover": { backgroundColor: "#1E3A5F", borderColor: "#1E3A5F", color: "#fff" },
  "&.Mui-disabled": { opacity: 0.6, color: "#1E3A5F" },
};
const ROW_OFF_SX = {
  ...ROW_ACTION_SX,
  color: "#B42318",
  backgroundColor: "#FEF3F2",
  border: "1px solid #FECDCA",
  "&:hover": { backgroundColor: "#B42318", borderColor: "#B42318", color: "#fff" },
  "&.Mui-disabled": { opacity: 0.6, color: "#B42318" },
};
const HEADER_BUTTON_SX = { height: "40px", gap: "8px", px: 2, whiteSpace: "nowrap" };

/**
 * RoadZen Users (owner, 2026-11-03): RoadZen's users ("sellers"), read from RoadZen through our backend every time the
 * page opens. Agency admins see them; the super admin also creates, activates and deactivates (`can.manage` comes from
 * the server). Only ONE user is active at a time: Activate switches the others off first (the server does it and
 * saves every change in our History).
 */
const RoadzenUsers = () => {
  const [users, setUsers] = useState(null);
  const [meta, setMeta] = useState({ apiEnv: null, can: { manage: false }, truncated: false });
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState("");
  // { [user id]: "ACTIVATE" | "DEACTIVATE" } — the spinner lives in that row; the row stays busy until the list is read again
  const [busyRows, setBusyRows] = useState({});
  const silentReadRef = useRef(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState(null); // { row, active }
  const [createOpen, setCreateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const silent = silentReadRef.current;
    silentReadRef.current = false;
    if (!silent) setLoading(true);
    listRoadzenUsers()
      .then((res) => {
        if (!alive) return;
        if (res.error) {
          setReadError(res.message);
          if (!silent) ToastError(res.message);
          return;
        }
        setReadError("");
        setUsers(res.data.users || []);
        setMeta({ apiEnv: res.data.apiEnv, can: res.data.can || { manage: false }, truncated: Boolean(res.data.truncated) });
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
        setBusyRows({});
      });
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  /** Reads RoadZen's list again without the full loader (after a change, or the Refresh button). */
  const refresh = () => {
    silentReadRef.current = true;
    setLoading(true);
    setRefreshKey((k) => k + 1);
  };

  const changeStatus = async (row, active) => {
    setBusyRows((prev) => ({ ...prev, [row.id]: active ? "ACTIVATE" : "DEACTIVATE" }));
    const res = await setRoadzenUserStatus(row.id, active);
    if (res.error) ToastError(res.message);
    else ToastSuccess(res.message);
    refresh();
  };

  const manage = Boolean(meta.can && meta.can.manage);
  const anyBusy = Object.keys(busyRows).length > 0;

  const rows = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (users || [])
      .filter((u) => {
        const text = `${u.name} ${u.mobile} ${u.code} ${u.email} ${u.city} ${u.state}`.toLowerCase();
        return words.every((w) => text.includes(w));
      })
      .map((u, index) => ({
        ...u,
        _id: String(u.id),
        sNo: index + 1,
        emailText: u.email || "—",
        cityText: u.city || "—",
        stateText: u.state || "—",
        walletText: walletText(u.walletBalance),
        createdText: dateText(u.createdAt),
        statusText: u.isActive ? "Active" : "Inactive",
      }));
  }, [users, search]);

  const columns = [
    { id: "sNo", label: "S.No", placeMent: true, disablePadding: false },
    { id: "code", label: "Code", placeMent: true, disablePadding: false },
    { id: "name", label: "Name", placeMent: true, disablePadding: false },
    { id: "mobile", label: "Mobile", placeMent: true, disablePadding: false },
    { id: "emailText", label: "Email", placeMent: true, disablePadding: false },
    { id: "cityText", label: "City", placeMent: true, disablePadding: false },
    { id: "stateText", label: "State", placeMent: true, disablePadding: false },
    { id: "walletText", label: "Wallet", placeMent: true, disablePadding: false },
    { id: "createdText", label: "Created Date", placeMent: true, disablePadding: false },
    { id: "statusText", label: "Status", placeMent: true, disablePadding: false, type: "coloredText", getColor: (row) => (row.isActive ? "green" : "grey") },
    ...(manage
      ? [
          {
            id: "action_more",
            label: "",
            placeMent: false,
            disablePadding: false,
            type: "component",
            renderComponent: (row) => {
              const active = !row.isActive;
              const label = active ? `Activate ${row.name}` : `Deactivate ${row.name}`;
              const Icon = active ? CheckCircleOutline : BlockOutlined;
              return (
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: "nowrap", justifyContent: "flex-end" }}>
                  <Tooltip title={active ? "Activate (the active user is switched off)" : "Deactivate"} placement="top" disableInteractive>
                    {/* a disabled button sends no mouse events, so the tooltip sits on this span */}
                    <span>
                      <IconButton size="small" aria-label={label} onClick={() => setConfirm({ row, active })} disabled={anyBusy} sx={active ? ROW_ACTION_SX : ROW_OFF_SX}>
                        {busyRows[row.id] ? <CircularProgress size={14} color="inherit" aria-label="Working on this row" /> : <Icon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              );
            },
          },
        ]
      : []),
  ];

  const warning = activeWarning(users);
  const activeUser = (users || []).find((u) => u.isActive) || null;
  const box = confirm ? confirmFor(confirm.row, users, confirm.active) : null;

  return (
    <div style={{ margin: "1px" }}>
      {/* Only the first read, when there is nothing to show yet. After that the rows stay on screen. */}
      {loading && users === null && <Loader open />}
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} justifyContent={"space-between"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography className="Master_Card_Tittle">
              RoadZen Users
              {meta.apiEnv === "TEST" && <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={8} className="d-flex" justifyContent={{ xs: "start", sm: "flex-end", md: "flex-end" }}>
            <Stack direction={{ xs: "column", sm: "row", md: "row" }} spacing={2} mt={{ xs: 2, sm: 2, md: 0 }}>
              {manage && (
                <Button className="Master_Card_Filter_Button" sx={HEADER_BUTTON_SX} startIcon={<PersonAddAltOutlined />} onClick={() => setCreateOpen(true)} disabled={anyBusy}>
                  Create User
                </Button>
              )}
              <Button className="Master_Card_Filter_Button" sx={HEADER_BUTTON_SX} startIcon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
                History
              </Button>
              <Button className="Master_Card_Filter_Button" sx={HEADER_BUTTON_SX} startIcon={<RefreshOutlined />} onClick={refresh} disabled={loading}>
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, mb: 2 }} />
        </Grid>

        {(readError || warning || meta.truncated) && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            {readError ? (
              <Alert severity="error">{readError}</Alert>
            ) : (
              <Alert severity="warning">
                {warning}
                {meta.truncated ? `${warning ? " " : ""}Only the newest 1000 RoadZen users are shown.` : ""}
              </Alert>
            )}
          </Grid>
        )}

        <Grid container alignItems={"center"} justifyContent={"flex-end"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3.1}>
            <Input
              className="w-100 Master_Card_Input"
              type="text"
              placeholder="Search name, mobile, code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disableUnderline={true}
              startAdornment={
                <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                  <IconButton aria-label="search" tabIndex={-1}>
                    <SearchIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
        </Grid>

        {/* a re-read: a thin bar over the rows, never a loader on top of them */}
        <Box sx={{ width: "100%", height: 3, mb: 0.5 }}>{loading && users !== null && <LinearProgress aria-label="Updating the list" sx={{ height: 3, borderRadius: 2 }} />}</Box>

        <MasterCommonTable
          data={rows}
          columns={columns}
          handleButton={{}}
          isServerSide={false}
          limit={10}
          tableConfig={{ isAction: false, isSearch: false, isView: false, isEdit: false, isDelete: false, isMultiDelete: false, isDownload: false }}
        />
      </Grid>

      {box && (
        <ConfirmBox
          open
          title={box.title}
          content={box.content}
          subContent={box.subContent}
          confirmButton={box.confirmButton}
          setOpenConfirmBox={() => setConfirm(null)}
          Function={() => {
            const { row, active } = confirm;
            setConfirm(null);
            changeStatus(row, active);
          }}
        />
      )}
      <CreateUserDialog open={createOpen} activeUser={activeUser} onClose={() => setCreateOpen(false)} onDone={refresh} />
      <HistoryDialog kind="users" open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
};

export default RoadzenUsers;
